/**
 * Utility functions for Grounding Search Visualization
 */

import {
	type CompetitorRanking,
	type GroundingSearchFilters,
	type GroundingSearchItem,
	type GroundingSearchKPIs,
	RANK_BANDS,
	type RankBand,
} from "./types";

/**
 * Get the rank band for a given position
 */
export function getRankBand(rank: number | null): RankBand {
	if (rank === null || rank > 30) return "unranked";
	if (rank <= 3) return "top3";
	if (rank <= 10) return "top10";
	if (rank <= 20) return "top20";
	return "top30";
}

/**
 * Get the rank band configuration for a rank
 */
export function getRankBandConfig(rank: number | null) {
	return RANK_BANDS[getRankBand(rank)];
}

/**
 * Get the best rank across engines (lowest position wins).
 */
export function getBestRank(
	rankGoogle: number | null,
	rankBrave: number | null,
): number | null {
	if (rankGoogle === null) return rankBrave;
	if (rankBrave === null) return rankGoogle;
	return Math.min(rankGoogle, rankBrave);
}

/**
 * The engine to display for an item. Claude grounds in Brave, so a Claude-only
 * search (googleUnavailable) shows Brave; everything else shows Google.
 */
export function getActiveEngine(item: GroundingSearchItem): "google" | "brave" {
	return item.googleUnavailable ? "brave" : "google";
}

/** Brand rank for the item's active engine. */
export function getBrandRankForItem(item: GroundingSearchItem): number | null {
	return item.googleUnavailable ? item.brandRankBrave : item.brandRankGoogle;
}

/** Brand URL for the item's active engine. */
export function getBrandUrlForItem(item: GroundingSearchItem): string | null {
	return item.googleUnavailable ? item.brandUrlBrave : item.brandUrlGoogle;
}

/** Competitor rank for the item's active engine. */
export function getCompetitorRankForItem(
	item: GroundingSearchItem,
	comp: CompetitorRanking,
): number | null {
	return item.googleUnavailable ? comp.rankBrave : comp.rankGoogle;
}

/** Competitor URL for the item's active engine. */
export function getCompetitorUrlForItem(
	item: GroundingSearchItem,
	comp: CompetitorRanking,
): string | null {
	return item.googleUnavailable ? comp.urlBrave : comp.urlGoogle;
}

/** Whether the item is a gap in either engine that ran. */
export function isGapForItem(item: GroundingSearchItem): boolean {
	return item.isGapGoogle || item.isGapBrave;
}

/**
 * Calculate KPIs from a list of grounding searches
 */
export function calculateKPIs(
	items: GroundingSearchItem[],
): GroundingSearchKPIs {
	const totalSearches = items.length;
	const gaps = items.filter(isGapForItem);
	const totalGaps = gaps.length;
	const rankedSearches = totalSearches - totalGaps;
	const highImpactGaps = gaps.filter((item) => item.impactScore >= 50).length;
	const avgImpactScore =
		items.length > 0
			? items.reduce((sum, item) => sum + item.impactScore, 0) /
				items.length
			: 0;

	// Calculate category coverage (unique query types with at least one ranking)
	const queryTypesWithRanking = new Set<string>();
	items.forEach((item) => {
		if (getBrandRankForItem(item) !== null) {
			item.discoveryQueries.forEach((dq) => {
				if (dq.queryType) {
					queryTypesWithRanking.add(dq.queryType);
				}
			});
		}
	});

	return {
		totalSearches,
		totalGaps,
		rankedSearches,
		highImpactGaps,
		avgImpactScore,
		categoryCoverage: queryTypesWithRanking.size,
	};
}

/**
 * Filter and sort grounding search items
 */
export function filterAndSortItems(
	items: GroundingSearchItem[],
	filters: GroundingSearchFilters,
): GroundingSearchItem[] {
	let filtered = [...items];

	// Drop `site:`-scoped rows FIRST, before any count or other predicate sees
	// them. They have no rank and no gap, so every filter below would treat them
	// as "unranked" and quietly inflate the gap and unranked-band counts.
	if (!filters.showSiteScoped) {
		filtered = filtered.filter((item) => !item.siteScopeDomain);
	}

	// Apply platform filter (source that generated the search)
	if (filters.platformFilter && filters.platformFilter !== "all") {
		filtered = filtered.filter(
			(item) => item.platforms?.includes(filters.platformFilter) ?? false,
		);
	}

	// Apply search filter
	if (filters.searchTerm) {
		const search = filters.searchTerm.toLowerCase();
		filtered = filtered.filter(
			(item) =>
				item.searchQuery.toLowerCase().includes(search) ||
				item.discoveryQueries.some((dq) =>
					dq.query.toLowerCase().includes(search),
				),
		);
	}

	// Apply search-engine filter (which engine's data to view)
	if (filters.searchEngine === "google") {
		filtered = filtered.filter((item) => !item.googleUnavailable);
	} else if (filters.searchEngine === "brave") {
		filtered = filtered.filter((item) => !item.braveUnavailable);
	}

	// Apply gap/ranked filter (against whichever engine ran for the item)
	if (filters.filterBy === "gaps") {
		filtered = filtered.filter(isGapForItem);
	} else if (filters.filterBy === "ranked") {
		filtered = filtered.filter(
			(item) => getBrandRankForItem(item) !== null,
		);
	}

	// Apply rank band filter
	if (filters.rankBandFilter !== "all") {
		const bandConfig = RANK_BANDS[filters.rankBandFilter];
		filtered = filtered.filter((item) => {
			const bestRank = getBrandRankForItem(item);
			if (bestRank === null) {
				return filters.rankBandFilter === "unranked";
			}
			if (bandConfig.max === null) {
				return bestRank >= bandConfig.min;
			}
			return bestRank >= bandConfig.min && bestRank <= bandConfig.max;
		});
	}

	// Apply competitor filter
	if (filters.selectedCompetitor) {
		filtered = filtered.filter((item) =>
			item.competitorRankings.some(
				(comp) => comp.competitorId === filters.selectedCompetitor,
			),
		);
	}

	// Sort
	filtered.sort((a, b) => {
		switch (filters.sortBy) {
			case "impact":
				return b.impactScore - a.impactScore;
			case "usage":
				return b.usageCount - a.usageCount;
			case "competitors":
				return (
					b.competitorRankings.length - a.competitorRankings.length
				);
			case "query":
				return a.searchQuery.localeCompare(b.searchQuery);
			default:
				return 0;
		}
	});

	return filtered;
}

/**
 * Get unique competitors from a list of items
 */
export function getUniqueCompetitors(items: GroundingSearchItem[]): Array<{
	id: string;
	name: string;
	domain: string;
}> {
	const competitorMap = new Map<
		string,
		{ id: string; name: string; domain: string }
	>();

	items.forEach((item) => {
		item.competitorRankings.forEach((comp) => {
			if (!competitorMap.has(comp.competitorId)) {
				competitorMap.set(comp.competitorId, {
					id: comp.competitorId,
					name: comp.competitorName,
					domain: comp.competitorDomain,
				});
			}
		});
	});

	return Array.from(competitorMap.values()).sort((a, b) =>
		a.name.localeCompare(b.name),
	);
}

/**
 * Get the best competitor rank for a search
 */
export function getBestCompetitorRank(
	competitorRankings: CompetitorRanking[],
	engine: "google" | "brave" | "all" = "google",
): { rank: number; competitorName: string } | null {
	let bestRank = Number.POSITIVE_INFINITY;
	let bestCompetitor = "";

	competitorRankings.forEach((comp) => {
		const ranks: Array<number | null> =
			engine === "google"
				? [comp.rankGoogle]
				: engine === "brave"
					? [comp.rankBrave]
					: [comp.rankGoogle, comp.rankBrave];
		for (const r of ranks) {
			if (r !== null && r < bestRank) {
				bestRank = r;
				bestCompetitor = comp.competitorName;
			}
		}
	});

	if (bestRank === Number.POSITIVE_INFINITY) return null;
	return { rank: bestRank, competitorName: bestCompetitor };
}

/**
 * Format a rank for display
 */
export function formatRank(rank: number | null): string {
	if (rank === null) return "-";
	return `#${rank}`;
}

/**
 * Calculate gap score between brand and best competitor
 */
export function calculateGapScore(
	brandRank: number | null,
	bestCompetitorRank: number | null,
): number {
	if (brandRank === null && bestCompetitorRank !== null) {
		// Brand not ranked but competitor is - high gap
		return 100 - Math.min(bestCompetitorRank * 2, 90);
	}
	if (brandRank !== null && bestCompetitorRank === null) {
		// Brand ranked but no competitors - no gap
		return 0;
	}
	if (brandRank === null || bestCompetitorRank === null) {
		return 0;
	}
	// Both ranked - calculate relative gap
	const gap = brandRank - bestCompetitorRank;
	if (gap <= 0) return 0; // Brand is ahead or equal
	return Math.min(gap * 5, 80);
}

/**
 * Get default filters
 */
export function getDefaultFilters(): GroundingSearchFilters {
	return {
		searchTerm: "",
		filterBy: "all",
		sortBy: "impact",
		searchEngine: "all",
		platformFilter: "all",
		selectedCompetitor: null,
		rankBandFilter: "all",
		// Hidden by default — see the field doc on GroundingSearchFilters.
		showSiteScoped: false,
	};
}
